import {
  Box,
  Button,
  Flex,
  List,
  ListItem,
  Text,
  useToast,
} from "@chakra-ui/react";
import React, {
  createContext,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { ViewState } from "react-map-gl";
import { useNavigate, useParams } from "react-router-dom";
import { Coordinate } from "~/generated/graphql.types";
import { Map } from "./parts/Map";
import {
  useFetchCourseQuery,
  useRoutingInfomationQuery,
  useTrialDriveQuery,
} from "./index.generated";
import { Sidebar } from "./parts/Sidebar";
import { ContentsEditor } from "./parts/ContentsEditor";
import { ImageEditor } from "./parts/ImageEditor";
import { SpotForRoutingFragment } from "~/routes/_shared/fragments.generated";
import { DraggableHyperlapsePlayer } from "./parts/Hyperlapse/DraggableHyperlapsePlayer";
import { DraggableStreetViewViewer } from "./parts/StreetView/DraggableStreetViewViewer";
import { RouteUsageGuide } from "./parts/routeUsageGuide/RouteUsageGuide";
import {
  DraggableTrialDrivePhotoViewer,
  TrialDriveMenu,
} from "./parts/TrialDrive";
import { DeleteRouteChangeAlertBox } from "./parts/DeleteRouteChangeAlert";

const Inner = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const params = useParams<{ courseId: string }>();
  const courseId = params.courseId!;
  const { editMode, isOpenHyperlapsePlayer, isOpenStreetViewViewer } =
    useContext(EditModeContext);

  const [fetchCourseRes] = useFetchCourseQuery({
    variables: { id: courseId },
  });

  if (fetchCourseRes.error?.message != undefined) {
    alert(fetchCourseRes.error?.message);
    navigate("/");
  }

  const [routingInfomationRes] = useRoutingInfomationQuery({
    variables: { courseId: courseId },
    requestPolicy: "network-only",
  });

  useEffect(() => {
    if (routingInfomationRes.data?.routingInfomation.error !== null) {
      console.error(routingInfomationRes.data?.routingInfomation.error);
      toast({
        title: "ルート情報の更新に失敗しました",
        description: routingInfomationRes.data?.routingInfomation.error,
        status: "error",
        duration: 2000,
        isClosable: true,
      });
      return;
    }
  }, [routingInfomationRes.data?.routingInfomation.error]);

  const course = fetchCourseRes.data!.course!;
  const routeChangeAlert = fetchCourseRes.data!.course.routeChangeAlert;
  course.spots = course.spots.sort((a, b) => (a.order > b.order ? 1 : -1));
  const start: SpotForRoutingFragment | undefined = useMemo(() => {
    if (course.spots.length < 1) return undefined;
    return course.spots[0];
  }, [course.spots]);
  const dest: SpotForRoutingFragment | undefined = useMemo(() => {
    if (course.spots.length < 2) return undefined;
    return course.spots[course.spots.length - 1];
  }, [course.spots]);
  const waypoints = useMemo(() => course.spots.slice(1, -1), [course.spots]);

  const [mapViewState, setMapViewState] = useState<Partial<ViewState>>({
    latitude: start ? start.coordinate.lat : 35.66708703846561,
    longitude: start ? start.coordinate.lon : 139.69350241388196,
    zoom: 14,
  });

  const [trialDriveId, setTrialDriveId] = useState<string>("");
  const [trialDriveRes, fetchTrialDrive] = useTrialDriveQuery({
    variables: { trialDriveId: trialDriveId! },
    pause: trialDriveId === "",
  });
  const [isOpenPhotoViewer, setIsOpenPhotoViewer] = useState<boolean>(false);
  const [photoUrl, setPhotoUrl] = useState<string | undefined>(undefined);

  const focusOnSpot = useCallback((coordinate: Coordinate) => {
    setMapViewState({
      ...mapViewState,
      latitude: coordinate.lat,
      longitude: coordinate.lon,
    });
  }, []);

  const [clickedLocation, setClickLocation] = useState<Coordinate>();

  const [showTrialDrive, setShowTrialDrive] = useState<boolean>(true);

  // 一時的にmapをDarkModeにするuseState.
  const isActiveChangeCourseAlertFlag = useMemo(() => {
    return fetchCourseRes.data?.appConfig.routeChangeAlert;
  }, [fetchCourseRes.data?.appConfig.routeChangeAlert]);
  const [isMapDarkMode, setIsMapDarkMode] = useState<boolean>(false);
  return (
    <>
      <Flex overflow="hidden">
        <Box width="500px" height="100vh">
          <Sidebar
            courseName={course.name}
            courseId={course.id}
            start={start}
            dest={dest}
            waypoints={waypoints}
            onClickSpot={focusOnSpot}
            isVisibleToAllUserGroups={course.isVisibleToAllUserGroups}
            statsAllSpots={
              routingInfomationRes.data?.routingInfomation.routingInfomation
                ?.spotStat
            }
            statsWithoutOpt={
              routingInfomationRes.data?.routingInfomation
                .routingInfomationWithoutOpt?.spotStat
            }
          />
        </Box>
        <Box position="relative">
          <Map
            courseId={course.id}
            isActiveChangeCourseAlertFlag={isActiveChangeCourseAlertFlag}
            routeChangeAlert={routeChangeAlert}
            isMapDarkMode={isMapDarkMode}
            viewState={mapViewState}
            onChangeViewState={setMapViewState}
            start={start}
            dest={dest}
            waypoints={waypoints}
            showTrialDrive={showTrialDrive}
            style={{
              width: "calc(100vw - 500px)",
              height: "100%",
              position: "fixed",
              color: "white",
            }}
            trialDriveSpots={trialDriveRes.data?.trialDrive.spots}
            trialDrivePassingPointSegments={
              trialDriveRes.data?.trialDrive.passingPointSegments
            }
            focusOnSpot={focusOnSpot}
            onClickLocation={(clickedCoordinate) =>
              setClickLocation(clickedCoordinate)
            }
            routeAllSpots={
              routingInfomationRes.data?.routingInfomation.routingInfomation
                ?.route
            }
            routeWithoutOpt={
              routingInfomationRes.data?.routingInfomation
                .routingInfomationWithoutOpt?.route
            }
            navigationPoints={
              routingInfomationRes.data?.routingInfomation.routingInfomation
                ?.navigation
            }
          />
          {editMode === "contents" ? <ContentsEditor /> : null}
          {editMode === "image" ? <ImageEditor courseId={courseId} /> : null}
        </Box>
        <Box
          position="absolute"
          bottom={editMode === "routing" ? 2.5 : 400 + 2.5}
          left={505}
        >
          <RouteUsageGuide />
        </Box>
        {isActiveChangeCourseAlertFlag && (
          // ここの時点で routeChangeAlert があるかないかで表示するか否かを決めると、
          // routeChangeAlertを消した瞬間にその結果を表示するModalも表示されなくなってしまうので、
          // DeleteRouteChangeAlertBoxの中で表示するか否かを決める。
          <Box position="absolute" top={"60px"} left={"510px"}>
            <DeleteRouteChangeAlertBox
              isShowBox={
                routeChangeAlert !== null && routeChangeAlert !== undefined
              }
              courseId={courseId}
              isMapDarkMode={isMapDarkMode}
              setIsMapDarkMode={setIsMapDarkMode}
            />
          </Box>
        )}
        <Box position="absolute" top={3} right={3}>
          <TrialDriveMenu
            userGroupId={course.userGroupId}
            showTrialDrive={showTrialDrive}
            isTrialDriveSelected={trialDriveId !== ""}
            trialDrive={trialDriveRes.data?.trialDrive}
            maxLength={editMode === "routing" ? "98vh" : "calc(98vh - 400px)"}
            onChangeTrialDriveId={(trialDriveId) => {
              setTrialDriveId(trialDriveId);
            }}
            switchShowTrialDrive={() => setShowTrialDrive(!showTrialDrive)}
            focusOnSpot={focusOnSpot}
            onClickPhoto={(photoUrl) => {
              setPhotoUrl(photoUrl);
              setIsOpenPhotoViewer(true);
            }}
          />
        </Box>
        {isOpenHyperlapsePlayer && (
          <DraggableHyperlapsePlayer courseId={course.id} />
        )}
        {isOpenStreetViewViewer && (
          <DraggableStreetViewViewer clickedCoordinate={clickedLocation} />
        )}
        {isOpenPhotoViewer && photoUrl && (
          <DraggableTrialDrivePhotoViewer
            photoUrl={photoUrl}
            onPushCloseButton={() => setIsOpenPhotoViewer(false)}
          />
        )}
      </Flex>
      {/* <Box position="absolute" top={2.5} right={600}>
        <ActivateHyperlapsePlayerButton courseId={courseId} />
      </Box>
      <Box position="absolute" top={2.5} right={275}>
        <ClearHyperlapseSpotsButton />
      </Box> */}
    </>
  );
};

export type EditMode = "routing" | "contents" | "image";

export const EditModeContext = createContext<{
  editMode: EditMode;
  isOpenHyperlapsePlayer: boolean;
  isOpenStreetViewViewer: boolean;
  spotIdForEdit?: string;
  courseIdForEdit?: string;
  switchToRoutingMode: () => void;
  switchToContentsMode: () => void;
  switchToImageMode: () => void;
  switchHyperlapsePlayer: (status: boolean) => void;
  switchStreetViewViewer: (status: boolean) => void;
  setSpotIdForEdit: (spotId?: string) => void;
}>({
  editMode: "routing",
  isOpenHyperlapsePlayer: false,
  isOpenStreetViewViewer: false,
  switchToRoutingMode: () => {},
  switchToContentsMode: () => {},
  switchToImageMode: () => {},
  switchHyperlapsePlayer: (status) => {},
  switchStreetViewViewer: (status) => {},
  setSpotIdForEdit: () => {},
});

export const EditingHyperlapseContext = createContext<{
  editingHyperlapseSpots: Coordinate[];
  setEditingHyperlapseSpots: (spots: Coordinate[]) => void;
  hyperlapseFrame:
    | { i: number; coordinate: Coordinate; heading: number }
    | undefined;
  setHyperlapseFrame: (frame: {
    i: number;
    coordinate: Coordinate;
    heading: number;
  }) => void;
}>({
  editingHyperlapseSpots: [],
  setEditingHyperlapseSpots: () => {},
  hyperlapseFrame: undefined,
  setHyperlapseFrame: () => {},
});

export const CourseDetail = () => {
  const [editMode, setEditMode] = useState<EditMode>("routing");
  const [spotIdForEdit, setSpotIdForEdit] = useState<string>();
  const switchToRoutingMode = useCallback(() => setEditMode("routing"), []);
  const switchToContentsMode = useCallback(() => setEditMode("contents"), []);
  const switchToImageMode = useCallback(() => setEditMode("image"), []);
  const [isOpenHyperlapsePlayer, setIsOpenHyperlapsePlayer] =
    useState<boolean>(false);
  const switchHyperlapsePlayer = useCallback((status: boolean) => {
    setIsOpenHyperlapsePlayer(status);
  }, []);
  const [isOpenStreetViewViewer, setIsOpenStreetViewViewer] =
    useState<boolean>(false);
  const switchStreetViewViewer = useCallback(
    (status: boolean) => setIsOpenStreetViewViewer(status),
    []
  );
  const [editingHyperlapseSpots, setEditingHyperlapseSpots] = useState<
    Coordinate[]
  >([]);
  const [hyperlapseFrame, setHyperlapseFrame] = useState<
    | {
        i: number;
        coordinate: Coordinate;
        heading: number;
      }
    | undefined
  >(undefined);

  return (
    <EditModeContext.Provider
      value={{
        editMode,
        isOpenHyperlapsePlayer,
        isOpenStreetViewViewer,
        spotIdForEdit,
        switchToContentsMode,
        switchToRoutingMode,
        switchToImageMode,
        switchHyperlapsePlayer,
        switchStreetViewViewer,
        setSpotIdForEdit,
      }}
    >
      <EditingHyperlapseContext.Provider
        value={{
          editingHyperlapseSpots,
          setEditingHyperlapseSpots,
          hyperlapseFrame,
          setHyperlapseFrame,
        }}
      >
        <Suspense fallback={<Text>Loading...</Text>}>
          <Inner />
        </Suspense>
      </EditingHyperlapseContext.Provider>
    </EditModeContext.Provider>
  );
};

export const UPPER_BOUND_LAT = 45.5;
export const LOWER_BOUND_LAT = 20.0;
export const UPPER_BOUND_LON = 154.0;
export const LOWER_BOUND_LON = 122.0;

export const validationLatLon = ({
  lat,
  lon,
}: {
  lat: number;
  lon: number;
}): {
  isValid: boolean;
  message: string | null;
} => {
  if (lat > UPPER_BOUND_LAT || lat < LOWER_BOUND_LAT) {
    return {
      isValid: false,
      message: `緯度は${LOWER_BOUND_LAT}以上${UPPER_BOUND_LAT}以下を指定してください。`,
    };
  }
  if (lon > UPPER_BOUND_LON || lon < LOWER_BOUND_LON) {
    return {
      isValid: false,
      message: `経度は${LOWER_BOUND_LON}以上${UPPER_BOUND_LON}以下を指定してください。`,
    };
  }
  return {
    isValid: true,
    message: null,
  };
};
